import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { db, auth, functions } from '../firebase';
import { collection, onSnapshot, query, doc, getDoc, setDoc, updateDoc, where, getDocs, documentId, orderBy, limit } from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { ipfsGateway, FIREBASE_PROJECT_ID, API_BASE_URL, BASE_MAX_STAMINA, TOKEN_CONTRACT, TOKENS, tokenContracts, tokenDecimals, buildingConfigClient, ATOMIC_API_ENDPOINTS, itemTemplates, EMBLEM_NAMES, MASTER_ACCOUNT_WAX_ID, ADMIN_ACCOUNTS, STAKING_TIERS, VAULT_TIERS, VAULT_TOKEN_INFO, VAULT_TOKEN_VALUE, getUpgradeCostClient, Client, calculateDiminishedBoost, calculateMaxHp, calculateXpForNextLevel } from '../constants/gameConfig';
import characterImage from '../assets/firecat.png';
import placeholderImage from '../assets/equip.png';
import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();


function HiLoJackpotPage({
    session,
    playerData,
    claimFaucetCallable,
    playHiLoCallable, // Left for backwards compatibility if needed
    playCrashCallable, // NEW: Call to initiate bet and get crash point
    cashOutCrashCallable, // NEW: Call to secure winnings if cashed out in time
    crashStatusCallable, // NEW: Call to poll if we crashed
    requestWithdrawalCallable,
    verifyDepositCallable,
    fetchTokenBalances
}) {
    const [jackpot, setJackpot] = useState(0);
    const [betAmount, setBetAmount] = useState('100');
    const [feedback, setFeedback] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [cooldown, setCooldown] = useState('');
    const [depositAmount, setDepositAmount] = useState('5000');
    const [localBalanceAdjustment, setLocalBalanceAdjustment] = useState(0); // Mocks balance change without backend

    // --- Crash Game State ---
    const [gameState, setGameState] = useState('idle'); // 'idle', 'launching', 'flying', 'crashed', 'cashed_out'
    const [multiplier, setMultiplier] = useState(1.00);
    const [cashedOutAt, setCashedOutAt] = useState(0);
    const [crashHistory, setCrashHistory] = useState([]);

    // --- Animation Refs ---
    const animationRef = useRef(null);
    const startTimeRef = useRef(0);
    const targetCrashPointRef = useRef(0);
    const currentMultiplierRef = useRef(1.00);
    const betAmountRef = useRef(0);

    // Fetch Grand Jackpot Pool
    useEffect(() => {
        const jackpotRef = doc(db, "game_data", "main_jackpot");
        const unsubscribe = onSnapshot(jackpotRef, (docSnap) => {
            if (docSnap.exists()) { setJackpot(docSnap.data().jackpotPool); }
        });
        return () => unsubscribe();
    }, []);

    // Faucet Cooldown Timer
    useEffect(() => {
        if (playerData?.isBlacklisted) return;
        const lastClaim = playerData?.lastFaucetClaim;
        if (!lastClaim) { setCooldown('Ready to Claim!'); return; }

        const intervalId = setInterval(() => {
            const now = new Date();
            const nextClaimTime = lastClaim.toDate().getTime() + 60 * 60 * 1000;
            if (now.getTime() >= nextClaimTime) {
                setCooldown('Ready to Claim!');
                clearInterval(intervalId);
            } else {
                const diff = nextClaimTime - now.getTime();
                const minutes = Math.floor((diff / 1000 / 60) % 60);
                const seconds = Math.floor((diff / 1000) % 60);
                setCooldown(`Claim in: ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`);
            }
        }, 1000);
        return () => clearInterval(intervalId);
    }, [playerData?.lastFaucetClaim, playerData?.isBlacklisted]);

    // Cleanup Animation on Unmount
    useEffect(() => {
        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, []);

    // Poll for crash status while flying
    useEffect(() => {
        let interval;
        if (gameState === 'flying' && crashStatusCallable) {
            interval = setInterval(async () => {
                try {
                    const result = await crashStatusCallable();
                    if (result.data.crashed) {
                        targetCrashPointRef.current = result.data.crashPoint;
                        cancelAnimationFrame(animationRef.current);
                        setMultiplier(result.data.crashPoint);
                        setGameState('crashed');
                        setFeedback(`💥 SHATTERED at ${result.data.crashPoint.toFixed(2)}x! You lost your bet.`);
                        addToHistory(result.data.crashPoint);
                    }
                } catch (e) {
                    console.error("Crash poll error:", e);
                }
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [gameState, crashStatusCallable]);

    // --- Core Operations ---
    const handleDeposit = async () => {
        const amount = parseInt(depositAmount, 10);
        if (isNaN(amount) || amount < 5000) {
            setFeedback("Error: Minimum deposit is 5,000 FIREMAN.");
            return;
        }
        if (!session) {
            setFeedback("Error: Wallet session not found.");
            return;
        }
        setIsLoading(true);
        setFeedback("Step 1 of 3: Preparing secure deposit...");
        const uniqueMemo = `DEPOSIT-${session.actor.toString()}-${Date.now()}`;
        const quantity = `${amount.toFixed(5)} FIREMAN`;
        try {
            setFeedback("Step 2 of 3: Please approve the transfer in your wallet.");
            const actions = [{
                account: tokenContracts['FIREMAN'],
                name: 'transfer',
                authorization: [session.permissionLevel],
                data: { from: session.actor.toString(), to: MASTER_ACCOUNT_WAX_ID, quantity, memo: uniqueMemo },
            }];
            const txResult = await session.transact({ actions });
            const txId = txResult.response.transaction_id;

            setFeedback("Transaction sent. Waiting a moment for the network to confirm...");
            await new Promise(resolve => setTimeout(resolve, 5000));
            setFeedback("Step 3 of 3: Verifying deposit on the blockchain...");

            const result = await verifyDepositCallable({ txId, memo: uniqueMemo });
            setFeedback(result.data.message);
            await fetchTokenBalances(session);
        } catch (error) {
            setFeedback(`Error: ${error.message}. Please try again.`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClaim = async () => {
        if (playerData?.isBlacklisted) return;
        setIsLoading(true);
        setFeedback('Claiming tokens...');
        try {
            const result = await claimFaucetCallable();
            setFeedback(result.data.message);
        } catch (error) {
            setFeedback(`Error: ${error.message}`);
        }
        setIsLoading(false);
    };

    const handleWithdraw = async () => {
        if (playerData?.isBlacklisted) return;
        if (!window.confirm("Are you sure you want to withdraw your entire in-game balance?")) return;
        setIsLoading(true);
        setFeedback("Processing withdrawal...");
        try {
            const result = await requestWithdrawalCallable();
            setFeedback(result.data.message);
            await fetchTokenBalances(session);
        } catch (error) {
            setFeedback(`Error: ${error.message}`);
        }
        setIsLoading(false);
    };

    // --- Crash Game Logic ---
    const addToHistory = (point) => {
        setCrashHistory(prev => [point, ...prev].slice(0, 10)); // Keep last 10
    };
    // ──────────────────────────────────────────────────────────────────
    // CRASH GAME LOGIC
    // ──────────────────────────────────────────────────────────────────

    const animateFlight = (timestamp) => {
        if (!startTimeRef.current) startTimeRef.current = timestamp;

        const now = Date.now();
        const elapsed = Math.max(0, now - startTimeRef.current);
        const currentM = Math.max(1.00, Math.exp(0.06 * (elapsed / 1000)));

        if (targetCrashPointRef.current && currentM >= targetCrashPointRef.current) {
            // Local Simulation crash
            currentMultiplierRef.current = targetCrashPointRef.current;
            setMultiplier(targetCrashPointRef.current);
            setGameState('crashed');
            setFeedback(`💥 CRASHED at ${targetCrashPointRef.current.toFixed(2)}x! You lost your bet.`);
            addToHistory(targetCrashPointRef.current);
        } else {
            if (gameState !== 'crashed') {
                currentMultiplierRef.current = currentM;
                setMultiplier(currentM);
                animationRef.current = requestAnimationFrame(animateFlight);
            }
        }
    };

    const handleLaunch = async () => {
        if (playerData?.isBlacklisted) return;
        const amount = parseInt(betAmount, 10);

        if (isNaN(amount) || amount < 1) {
            setFeedback("Error: Invalid bet amount.");
            return;
        }
        if (((playerData?.faucetBalance || 0) + localBalanceAdjustment) < amount) {
            setFeedback("Error: Insufficient FIREMAN balance.");
            return;
        }

        setIsLoading(true);
        setFeedback("Igniting Thrusters...");

        try {
            let finalCrashPoint = 0;

            // Call Backend to deduct bet and initiate session
            if (playCrashCallable) {
                const result = await playCrashCallable({ betAmount: amount });
                // We no longer receive the exact crash point to prevent cheating.
                // The backend just tells us it started.
                finalCrashPoint = 1000; // Visual dummy max
            } else {
                // FALLBACK: Simulate crash point visually if backend is not hooked up yet
                const r = Math.random();
                finalCrashPoint = Math.max(1.00, (0.95 / r)); // 95% RTP Simulation
                if (finalCrashPoint > 1000) finalCrashPoint = 1000; // Cap visual simulation
                setLocalBalanceAdjustment(prev => prev - amount); // Mock deduction
            }

            betAmountRef.current = amount;
            targetCrashPointRef.current = finalCrashPoint;

            setGameState('launching');
            setMultiplier(1.00);
            currentMultiplierRef.current = 1.00;

            // Brief launch delay
            setTimeout(() => {
                setGameState('flying');
                setFeedback("🚀 Flying! Cash out before the rocket crashes!");
                if (playCrashCallable) {
                    startTimeRef.current = Date.now();
                } else {
                    startTimeRef.current = Date.now();
                }
                animationRef.current = requestAnimationFrame(animateFlight);
                setIsLoading(false);
            }, 1000);

        } catch (error) {
            setFeedback(`Launch Error: ${error.message}`);
            setGameState('idle');
            setIsLoading(false);
        }
    };

    const handleCashOut = async () => {
        if (gameState !== 'flying') return;

        // Halt animation immediately
        cancelAnimationFrame(animationRef.current);
        const cashOutMult = currentMultiplierRef.current;

        setGameState('cashed_out');
        setCashedOutAt(cashOutMult);
        setFeedback(`✅ Cashed out at ${cashOutMult.toFixed(2)}x! Securing winnings...`);
        setIsLoading(true);

        try {
            // Call Backend to verify and award tokens
            if (cashOutCrashCallable) {
                const result = await cashOutCrashCallable({ cashOutMultiplier: cashOutMult });
                if (result.data.success) {
                    setFeedback(`🎉 SECURED! You won ${result.data.amountWon.toLocaleString()} FIREMAN!`);
                } else {
                    // This means the server rejected our cashout because it already crashed.
                    setGameState('crashed');
                    setMultiplier(cashOutMult); // Show where we tried to stop
                    setFeedback(result.data.message || `💥 CRASHED! You lost your bet.`);
                }
            } else {
                // FALLBACK: Simulate win visually
                const won = Math.floor(betAmountRef.current * cashOutMult);
                setFeedback(`🎉 SECURED! You won ${won.toLocaleString()} FIREMAN! (Simulation)`);
                setLocalBalanceAdjustment(prev => prev + won); // Mock win addition
            }
        } catch (error) {
            setFeedback(`Cash Out Error: ${error.message}`);
        }

        addToHistory(targetCrashPointRef.current);
        setIsLoading(false);
    };

    // Calculate Rocket Position (Logarithmic scaling so it stays on screen longer)
    const xPos = Math.min(85, Math.log10(multiplier) * 45);
    const yPos = Math.min(80, Math.log10(multiplier) * 40);

    return (
        <div style={{ backgroundColor: 'rgba(0,0,0,0.7)', padding: '20px', borderRadius: '8px', maxWidth: '800px', margin: 'auto', textAlign: 'center' }}>

            {/* Grand Jackpot Banner */}
            <div style={{ border: '2px solid gold', padding: '10px', borderRadius: '8px' }}>
                <h2 style={{ color: 'gold', margin: 0 }}>GRAND JACKPOT</h2>
                <h1 style={{ color: 'white', margin: '5px 0', fontSize: '3em' }}>{Math.floor(jackpot).toLocaleString()} FIREMAN</h1>
                <p style={{ margin: 0, color: '#ccc' }}>Any rocket launch could randomly trigger the Grand Jackpot!</p>
            </div>

            <hr style={{ margin: '20px 0', borderColor: '#444' }} />

            {/* Thruster Station & Funds Section */}
            <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '20px' }}>
                <div style={{ flex: 1, minWidth: '300px' }}>
                    <div style={{ marginTop: '20px', borderTop: '1px dashed #444', paddingTop: '15px' }}>
                        <h4 style={{ color: '#aaa', margin: '0 0 5px 0' }}>Standard Faucet Allowance</h4>
                        <button
                            onClick={handleClaim}
                            disabled={isLoading || gameState === 'flying' || cooldown !== 'Ready to Claim!'}
                            className="modern-button"
                            style={{ margin: 0, padding: '10px 20px', fontSize: '14px' }}
                        >
                            {cooldown}
                        </button>
                    </div>
                </div>

                <div style={{ flex: 1, minWidth: '300px' }}>
                    <h3 style={{ color: '#00e5ff' }}>Reserve Vault</h3>
                    {!isNative ? (
                        <>
                            <p style={{ fontSize: '14px', color: '#ccc', margin: '0 0 10px 0' }}>Withdrawn today: {(playerData?.dailyWithdrawal?.amount || 0).toLocaleString()} / 100,000</p>
                            <p style={{ fontSize: '14px', color: '#ccc', margin: '0 0 10px 0' }}>Minimum withdrawal: {(35000).toLocaleString()} FIREMAN</p>
                            <input
                                type="number"
                                value={depositAmount}
                                onChange={e => setDepositAmount(e.target.value)}
                                min="5000"
                                placeholder="Min 5,000"
                                style={{ padding: '8px', fontSize: '16px', width: '100%', boxSizing: 'border-box', background: '#222', color: 'white', border: '1px solid #555', borderRadius: '4px', marginBottom: '10px' }}
                            />
                            <button onClick={handleDeposit} disabled={isLoading || gameState === 'flying'} className="modern-button" style={{ width: '100%', backgroundColor: 'darkgreen' }}>
                                Deposit FIREMAN
                            </button>
                            <button
                                onClick={handleWithdraw}
                                disabled={isLoading || gameState === 'flying' || ((playerData?.faucetBalance || 0) + localBalanceAdjustment) < 35000 || ((playerData?.dailyWithdrawal?.amount || 0) + (playerData?.faucetBalance || 0) + localBalanceAdjustment) > 100000}
                                className="modern-button"
                                style={{ width: '100%', backgroundColor: 'darkred' }}
                            >
                                Withdraw {Math.floor((playerData?.faucetBalance || 0) + localBalanceAdjustment).toLocaleString()} FIREMAN
                            </button>
                        </>
                    ) : (
                        <div style={{ padding: '16px', background: 'rgba(0, 229, 255, 0.08)', border: '1px dashed rgba(0, 229, 255, 0.3)', borderRadius: '8px', textAlign: 'center', marginTop: '10px' }}>
                            <p style={{ color: '#00e5ff', fontWeight: 'bold', margin: '0 0 8px 0', fontSize: '14px' }}>🌐 Web Portal Vault</p>
                            <p style={{ color: '#aaa', fontSize: '12px', margin: 0, lineHeight: '1.5' }}>
                                On-chain FIREMAN token deposits and withdrawals are managed securely via our official Web Portal at <strong style={{ color: '#fff' }}>rewardsinwhatif.app</strong> to comply with Google Play Developer policies.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <hr style={{ margin: '30px 0', borderColor: '#444' }} />

            {/* CRASH GAME SECTION */}
            <h3 style={{ fontSize: '2em', margin: '0 0 10px 0', color: '#ff9800' }}>🚀 ROCKET CRASH</h3>
            <p>Your In-Game Balance: <b style={{ color: 'cyan' }}>{Math.floor((playerData?.faucetBalance || 0) + localBalanceAdjustment).toLocaleString()} FIREMAN</b></p>

            {/* History Bar */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '15px', overflowX: 'auto', padding: '5px', justifyContent: 'center' }}>
                {crashHistory.map((pt, i) => (
                    <div key={i} style={{ padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', backgroundColor: pt >= 2.0 ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 75, 43, 0.2)', border: `1px solid ${pt >= 2.0 ? '#4caf50' : '#ff4b2b'}`, color: pt >= 2.0 ? '#4caf50' : '#ff4b2b' }}>
                        {pt.toFixed(2)}x
                    </div>
                ))}
            </div>

            {/* Visual Arena */}
            <div style={{
                height: '350px',
                background: 'linear-gradient(to bottom, #0b0f19 0%, #1a233a 100%)',
                position: 'relative',
                borderRadius: '12px',
                overflow: 'hidden',
                border: gameState === 'crashed' ? '2px solid #ff4b2b' : gameState === 'cashed_out' ? '2px solid #4caf50' : '2px solid #555',
                boxShadow: gameState === 'flying' ? 'inset 0 0 20px rgba(0, 229, 255, 0.2)' : 'none'
            }}>

                {/* Multiplier Display */}
                <div style={{
                    position: 'absolute',
                    top: '30px',
                    left: '0',
                    right: '0',
                    textAlign: 'center',
                    fontSize: '64px',
                    fontWeight: '900',
                    color: gameState === 'crashed' ? '#ff4b2b' : gameState === 'cashed_out' ? '#4caf50' : 'white',
                    textShadow: '0 4px 15px rgba(0,0,0,0.5)',
                    fontFamily: 'monospace'
                }}>
                    {multiplier.toFixed(2)}x
                </div>

                {/* Status Subtext */}
                <div style={{ position: 'absolute', top: '110px', left: '0', right: '0', textAlign: 'center', fontSize: '18px', color: '#aaa', fontWeight: 'bold' }}>
                    {gameState === 'idle' && 'Awaiting Launch'}
                    {gameState === 'launching' && 'Igniting...'}
                    {gameState === 'flying' && <span style={{ color: '#ff9800', animation: 'pulse 1s infinite' }}>Flying...</span>}
                    {gameState === 'crashed' && 'CRASHED!'}
                    {gameState === 'cashed_out' && `Secured at ${cashedOutAt.toFixed(2)}x`}
                </div>

                {/* The Rocket */}
                <div style={{
                    position: 'absolute',
                    left: `${xPos}%`,
                    bottom: `${yPos}%`,
                    fontSize: '50px',
                    transition: gameState === 'flying' ? 'none' : 'all 0.5s ease',
                    transform: gameState === 'crashed' ? 'rotate(120deg) scale(0.5)' : 'rotate(45deg)',
                    opacity: gameState === 'crashed' ? 0 : 1,
                    filter: gameState === 'flying' ? 'drop-shadow(-5px 5px 10px rgba(255, 152, 0, 0.8))' : 'none',
                    zIndex: 10
                }}>
                    🚀
                </div>

                {/* Explosion */}
                {gameState === 'crashed' && (
                    <div style={{
                        position: 'absolute',
                        left: `${xPos}%`,
                        bottom: `${yPos}%`,
                        fontSize: '80px',
                        transform: 'translate(-20%, 20%)',
                        zIndex: 5
                    }}>
                        💥
                    </div>
                )}

                {/* Stars/Grid Background effect */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%', background: 'linear-gradient(transparent, rgba(0, 229, 255, 0.1))', borderTop: '1px dashed rgba(0, 229, 255, 0.3)' }}></div>
            </div>

            {/* Game Controls */}
            <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'center', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ textAlign: 'left' }}>
                    <label style={{ display: 'block', fontSize: '14px', color: '#00e5ff', marginBottom: '5px', fontWeight: 'bold' }}>Bet Amount</label>
                    <input
                        type="number"
                        value={betAmount}
                        onChange={(e) => setBetAmount(e.target.value)}
                        disabled={gameState === 'launching' || gameState === 'flying'}
                        style={{ padding: '15px', fontSize: '18px', width: '180px', background: 'rgba(0,0,0,0.5)', border: '2px solid #555', color: 'white', borderRadius: '8px', outline: 'none' }}
                    />
                </div>

                {gameState === 'flying' ? (
                    <button
                        onClick={handleCashOut}
                        disabled={isLoading}
                        className="modern-button"
                        style={{ background: 'linear-gradient(145deg, #28a745, #218838)', padding: '15px 40px', fontSize: '24px', margin: 0, marginTop: '20px', boxShadow: '0 0 20px rgba(76, 175, 80, 0.5)' }}
                    >
                        🛑 CASH OUT
                    </button>
                ) : (
                    <button
                        onClick={handleLaunch}
                        disabled={gameState === 'launching' || isLoading}
                        className="modern-button"
                        style={{ background: 'linear-gradient(145deg, #ff9800, #f57c00)', padding: '15px 40px', fontSize: '24px', margin: 0, marginTop: '20px', boxShadow: '0 0 20px rgba(255, 152, 0, 0.5)' }}
                    >
                        🚀 LAUNCH
                    </button>
                )}
            </div>

            {/* Feedback / Logs */}
            {feedback && (
                <div style={{ marginTop: '25px', padding: '15px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', borderLeft: feedback.includes('Error') || feedback.includes('lost') ? '4px solid #ff4b2b' : '4px solid #4caf50' }}>
                    <p style={{ margin: 0, fontSize: '16px', color: feedback.includes('Error') || feedback.includes('lost') ? '#ff4b2b' : '#4caf50', fontWeight: 'bold' }}>{feedback}</p>
                </div>
            )}

            <style>{`
                @keyframes pulse {
                    0% { opacity: 1; }
                    50% { opacity: 0.5; }
                    100% { opacity: 1; }
                }
            `}</style>
        </div>
    );
}

export default HiLoJackpotPage;
